// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import S44ImportModal from '../components/S44ImportModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp, Calculator, FileDown, Save, Trash2, Plus, History, Wrench, FolderSearch, FileSpreadsheet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_STORAGE_KEY = 'maintenanceForecast:v1';

// S-44 recomenda o Ano de Serviço iniciando em Julho.
const MONTHS = [
  { key: 'jul', label: 'Julho' },
  { key: 'ago', label: 'Agosto' },
  { key: 'set', label: 'Setembro' },
  { key: 'out', label: 'Outubro' },
  { key: 'nov', label: 'Novembro' },
  { key: 'dez', label: 'Dezembro' },
  { key: 'jan', label: 'Janeiro' },
  { key: 'fev', label: 'Fevereiro' },
  { key: 'mar', label: 'Março' },
  { key: 'abr', label: 'Abril' },
  { key: 'mai', label: 'Maio' },
  { key: 'jun', label: 'Junho' },
];

const emptyExpenses = () =>
  MONTHS.reduce((acc, m) => ({ ...acc, [m.key]: '' }), {});

const currentServiceYearGuess = () => {
  const now = new Date();
  const year = now.getFullYear();
  // Se estamos em Julho ou depois, o ano de serviço termina no ano seguinte.
  return now.getMonth() >= 6 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

const formatBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

export default function MaintenanceForecastPage() {
  const [serviceYear, setServiceYear] = useState(currentServiceYearGuess());
  const [expenses, setExpenses] = useState(emptyExpenses);
  const [history, setHistory] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [fillingFromForms, setFillingFromForms] = useState(false);

  const handleFillFromForms = async () => {
    if (!/^\s*20\d{2}\s*[-/]\s*20\d{2}\s*$/.test(serviceYear)) {
      toast.error('Informe o Ano de Serviço no formato 2026-2027');
      return;
    }
    setFillingFromForms(true);
    try {
      const { data } = await axios.get(`${API}/s44-forecasts/from-forms`, {
        params: { service_year: serviceYear.trim() },
      });
      const entries = Object.entries(data.values || {});
      if (entries.length === 0) {
        toast.info('Nenhum S-44 digital encontrado para este Ano de Serviço');
        return;
      }
      setExpenses((prev) => {
        const next = { ...prev };
        entries.forEach(([key, num]) => { next[key] = String(num); });
        return next;
      });
      toast.success(`${entries.length} mês(es) preenchidos com o Total das despesas dos S-44`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar S-44 digitais');
    } finally {
      setFillingFromForms(false);
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/s44-forecasts`);
      let list = data.forecasts || [];

      // Migração única: envia previsões antigas do localStorage para o servidor.
      const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (raw) {
        try {
          const legacy = JSON.parse(raw);
          const existingYears = new Set(list.map((f) => f.service_year));
          for (const rec of legacy) {
            if (!existingYears.has(rec.service_year)) {
              const { data: saved } = await axios.post(`${API}/s44-forecasts`, {
                id: rec.id,
                service_year: rec.service_year,
                expenses: rec.expenses || {},
                totals: rec.totals || {},
              });
              list = [saved, ...list];
            }
          }
          if (legacy.length > 0) {
            toast.info('Previsões locais migradas para a nuvem');
          }
        } catch {
          // localStorage corrompido — ignora
        }
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      }

      list.sort((a, b) => (b.service_year || '').localeCompare(a.service_year || ''));
      setHistory(list);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar previsões salvas');
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const numericValues = useMemo(
    () => MONTHS.map((m) => Number(expenses[m.key]) || 0),
    [expenses]
  );

  const totals = useMemo(() => {
    const total = numericValues.reduce((a, b) => a + b, 0);
    const average = total / 12;
    return {
      total,
      average,
      goalMin: average * 2,
      goalMax: average * 3,
    };
  }, [numericValues]);

  const filledCount = numericValues.filter((v) => v > 0).length;

  const handleExpenseChange = (key, value) => {
    // Aceita apenas números e ponto/vírgula. Armazena string para permitir edição livre.
    const cleaned = value.replace(',', '.');
    if (cleaned === '' || /^[0-9]*\.?[0-9]*$/.test(cleaned)) {
      setExpenses((prev) => ({ ...prev, [key]: cleaned }));
    }
  };

  const resetForm = useCallback(() => {
    setServiceYear(currentServiceYearGuess());
    setExpenses(emptyExpenses());
    setCurrentId(null);
  }, []);

  const handleSave = async () => {
    if (!serviceYear.trim()) {
      toast.error('Informe o Ano de Serviço');
      return;
    }
    try {
      const { data: saved } = await axios.post(`${API}/s44-forecasts`, {
        id: currentId,
        service_year: serviceYear.trim(),
        expenses,
        totals,
      });
      const others = history.filter((h) => h.id !== saved.id);
      const next = [saved, ...others].sort((a, b) =>
        (b.service_year || '').localeCompare(a.service_year || '')
      );
      setHistory(next);
      setCurrentId(saved.id);
      toast.success(`Previsão de ${saved.service_year} salva na nuvem`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao salvar previsão');
    }
  };

  const handleLoad = (id) => {
    const rec = history.find((h) => h.id === id);
    if (!rec) return;
    setServiceYear(rec.service_year);
    setExpenses({ ...emptyExpenses(), ...rec.expenses });
    setCurrentId(rec.id);
    toast.info(`Carregado: ${rec.service_year}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta previsão do histórico?')) return;
    try {
      await axios.delete(`${API}/s44-forecasts/${id}`);
      setHistory((prev) => prev.filter((h) => h.id !== id));
      if (currentId === id) resetForm();
      toast.success('Previsão removida');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao remover previsão');
    }
  };

  const handleImport = (values) => {
    if (!values) return;
    setExpenses((prev) => {
      const next = { ...prev };
      Object.entries(values).forEach(([key, num]) => {
        if (MONTHS.some((m) => m.key === key)) {
          next[key] = String(num);
        }
      });
      return next;
    });
  };

  const handleExportPdf = async () => {
    try {
      toast.loading('Gerando PDF...', { id: 'forecast-pdf' });
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.text('Previsão de Gastos — Manutenção (S-44)', 14, 20);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Ano de Serviço: ${serviceYear}`, 14, 28);
      doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, 14, 34);

      autoTable(doc, {
        startY: 42,
        head: [['Mês', 'Despesa (R$)']],
        body: MONTHS.map((m, i) => [m.label, formatBRL(numericValues[i])]),
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });

      const afterTable = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Resultado do cálculo', 14, afterTable);

      autoTable(doc, {
        startY: afterTable + 4,
        body: [
          ['Total anual (Jul → Jun)', formatBRL(totals.total)],
          ['Média mensal (÷ 12)', formatBRL(totals.average)],
          ['Meta mínima (média × 2)', formatBRL(totals.goalMin)],
          ['Meta máxima (média × 3)', formatBRL(totals.goalMax)],
        ],
        styles: { fontSize: 11, cellPadding: 4 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 90 },
          1: { halign: 'right' },
        },
        theme: 'grid',
      });

      const footY = doc.lastAutoTable.finalY + 12;
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(
        'Fonte: instruções do formulário S-44. A meta mínima corresponde a 2 meses',
        14,
        footY
      );
      doc.text(
        'de despesa média; a meta máxima a 3 meses.',
        14,
        footY + 5
      );

      doc.save(`Previsao_Gastos_S44_${serviceYear.replace(/\//g, '-')}.pdf`);
      toast.success('PDF exportado!', { id: 'forecast-pdf' });
    } catch (err) {
      console.error(err);
      toast.error('Erro ao exportar PDF', { id: 'forecast-pdf' });
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="maintenance-forecast-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground font-heading flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-emerald-600" />
              Previsão de Gastos
            </h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Manutenção — baseada nas instruções do S-44 (Julho a Junho)
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleFillFromForms}
              disabled={fillingFromForms}
              data-testid="fill-from-s44-forms-button"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {fillingFromForms ? 'Buscando…' : 'Preencher do S-44'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setImportOpen(true)}
              data-testid="import-from-reports-button"
            >
              <FolderSearch className="w-4 h-4 mr-2" />
              Importar de Relatórios
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetForm}
              data-testid="new-forecast-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova previsão
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPdf}
              data-testid="export-pdf-button"
              disabled={filledCount === 0}
            >
              <FileDown className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
            <Button onClick={handleSave} size="sm" data-testid="save-forecast-button">
              <Save className="w-4 h-4 mr-2" />
              Salvar
            </Button>
          </div>
        </div>

        {/* Header inputs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Identificação do período
            </CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs">Ano de Serviço</Label>
              <Input
                value={serviceYear}
                onChange={(e) => setServiceYear(e.target.value)}
                placeholder="Ex: 2026-2027"
                data-testid="service-year-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Digite o intervalo do Ano de Serviço no formato livre (ex.: &quot;2026-2027&quot;).
              </p>
            </div>
            <div className="flex items-end">
              <Badge variant="outline" className="w-full justify-center py-2">
                Preenchidos: {filledCount}/12
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Monthly inputs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Despesas mensais</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {MONTHS.map((m) => (
                <div key={m.key}>
                  <Label className="text-xs">{m.label}</Label>
                  <Input
                    inputMode="decimal"
                    value={expenses[m.key]}
                    onChange={(e) => handleExpenseChange(m.key, e.target.value)}
                    placeholder="0,00"
                    data-testid={`expense-${m.key}-input`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="forecast-results">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Total anual (Jul → Jun)</p>
              <p className="text-2xl font-bold mt-1" data-testid="total-value">
                {formatBRL(totals.total)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Média mensal (÷ 12)</p>
              <p className="text-2xl font-bold mt-1 text-blue-700" data-testid="average-value">
                {formatBRL(totals.average)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/40 dark:bg-amber-950/20">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Meta mínima (× 2)</p>
              <p className="text-2xl font-bold mt-1 text-amber-700" data-testid="goal-min-value">
                {formatBRL(totals.goalMin)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">
                2 meses de despesa média
              </p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/20">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Meta máxima (× 3)</p>
              <p className="text-2xl font-bold mt-1 text-emerald-700" data-testid="goal-max-value">
                {formatBRL(totals.goalMax)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">
                3 meses de despesa média
              </p>
            </CardContent>
          </Card>
        </div>

        {/* History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico
              <Badge variant="outline" data-testid="history-count">
                {history.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma previsão salva ainda. Preencha os campos e clique em &quot;Salvar&quot;.
              </p>
            ) : (
              <div className="space-y-2">
                {history.map((h) => (
                  <div
                    key={h.id}
                    className={`flex items-center justify-between gap-3 rounded-xl border p-3 transition-colors ${
                      currentId === h.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/40'
                    }`}
                    data-testid={`history-row-${h.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">
                        Ano de Serviço {h.service_year}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Média {formatBRL(h.totals?.average)} · Mín {formatBRL(h.totals?.goalMin)} · Máx {formatBRL(h.totals?.goalMax)}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Atualizado em {new Date(h.updated_at).toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleLoad(h.id)}
                        data-testid={`load-forecast-${h.id}`}
                      >
                        Carregar
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(h.id)}
                        className="text-red-600"
                        data-testid={`delete-forecast-${h.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center">
          As previsões são salvas na nuvem e sincronizadas entre todos os seus dispositivos.
        </p>
      </div>

      <S44ImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={handleImport}
      />
    </Layout>
  );
}
